import React, { useState, useMemo, useEffect, useRef } from "react";
import { Search, Filter, AlertCircle, Clock, Loader2 } from "lucide-react";
import { Card, SectionLabel } from "../components/ui-bits";
import { liveAPI } from "../data/liveApi";
import { Chip } from "../components/journal/JournalChips";
import EventCard from "../components/events/EventCard";
import { SeoHead } from "../seo/SeoHead";

const CATEGORIES = [
  "ALL", "RBI", "SEBI", "NSE", "BSE", "Government Policy", "Corporate Earnings",
  "US Markets", "Global Markets", "Oil", "Currencies", "Bond Yields", "Geopolitics",
];
const SEVERITIES = ["ALL", "high", "medium", "low"];
const EMPTY_RECENT_MSG = "No significant market events have been reported in the last 48 hours.";

function severityForImpact(impact) {
  if (impact >= 7) return "high";
  if (impact >= 5) return "medium";
  return "low";
}

function formatPublishedAt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" });
}

function mapNewsToEvent(n, i, analysisByHeadline) {
  const ai = analysisByHeadline.get((n.headline || "").trim().toLowerCase());
  return {
    id: n.id || `live-${i}`,
    key: n.key || null,
    title: n.headline,
    summary: n.summary,
    region: n.country || "—",
    asset: n.category,
    severity: severityForImpact(n.impact),
    time: formatPublishedAt(n.published_at),
    published_at: n.published_at,
    tickers: n.tickers || [],
    url: n.url,
    live: true,
    ai,
  };
}

function UnconfiguredBanner() {
  return (
    <Card className="flex items-start gap-3 border-fij-amber/40 bg-fij-amber/5">
      <AlertCircle size={16} className="text-fij-amber shrink-0 mt-0.5" />
      <div className="font-mono text-xs text-fij-amber">
        <div className="text-fij-amber font-medium">Live news provider not configured.</div>
        <div className="text-fij-textDim mt-0.5">
          Add <span className="text-fij-text">MARKETAUX_API_KEY</span> to{" "}
          <span className="text-fij-text">/app/backend/.env</span> to start the live feed.
        </div>
      </div>
    </Card>
  );
}

function FilterChipGroup({ label, items, active, onSelect, testIdPrefix }) {
  return (
    <div>
      <SectionLabel>{label}</SectionLabel>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {items.map((r) => (
          <Chip
            key={r}
            testId={`${testIdPrefix}-${r.replace(/\s+/g, "-")}`}
            active={active === r}
            onClick={() => onSelect(r)}
          >
            {r}
          </Chip>
        ))}
      </div>
    </div>
  );
}

function FilterBar({ q, setQ, cat, setCat, severity, setSeverity }) {
  return (
    <Card>
      <div className="flex flex-col lg:flex-row gap-4 lg:items-center">
        <div className="flex items-center gap-2 bg-fij-bg border border-fij-border rounded-sm px-2.5 h-9 flex-1 min-w-0">
          <Search size={14} className="text-fij-textMuted" />
          <input
            data-testid="events-search"
            placeholder="Search companies, indices, sectors, themes, countries…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="bg-transparent text-sm font-mono placeholder:text-fij-textMuted outline-none w-full text-fij-text"
          />
        </div>
        <div className="flex items-center gap-2 text-fij-textMuted">
          <Filter size={12} />
          <span className="font-mono text-[10px] uppercase">Filters</span>
        </div>
      </div>

      <div className="mt-4 space-y-4">
        <FilterChipGroup label="Category" items={CATEGORIES} active={cat} onSelect={setCat} testIdPrefix="filter-cat" />
        <FilterChipGroup label="Severity" items={SEVERITIES} active={severity} onSelect={setSeverity} testIdPrefix="filter-severity" />
      </div>
    </Card>
  );
}

function EmptyState({ message }) {
  return (
    <Card
      className="sm:col-span-2 lg:col-span-3 text-center py-12 text-fij-textMuted font-mono text-sm flex flex-col items-center gap-2"
      data-testid="events-empty-state"
    >
      <Clock size={18} className="text-fij-textMuted" />
      <div>{message}</div>
    </Card>
  );
}

export default function GlobalEvents() {
  const [cat, setCat] = useState("ALL");
  const [severity, setSeverity] = useState("ALL");
  const [q, setQ] = useState("");

  // Paginated live news — accumulate pages, dedupe by id, auto-refresh first page every 5 min.
  const [pages, setPages] = useState({});      // { 1: {news, meta,...}, 2: {...} }
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [analysesData, setAnalysesData] = useState(null);
  const abortRef = useRef(null);

  const fetchPage = async (page, resetOnSuccess = false) => {
    setError(null);
    if (page === 1) setLoading(true); else setLoadingMore(true);
    try {
      const data = await liveAPI.news(cat === "ALL" ? null : cat, { limit: 100, page });
      setPages((prev) => (resetOnSuccess ? { [page]: data } : { ...prev, [page]: data }));
      if (page > currentPage) setCurrentPage(page);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load news feed.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Load page 1 whenever category changes; also refresh every 5 min.
  useEffect(() => {
    setPages({});
    setCurrentPage(1);
    fetchPage(1, true);
    const iv = setInterval(() => fetchPage(1, true), 300_000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cat]);

  // AI analyses (for badges) — refresh every 5 min
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const d = await liveAPI.analyses({ limit: 200 });
        if (mounted) setAnalysesData(d);
      } catch { /* silent */ }
    };
    load();
    const iv = setInterval(load, 300_000);
    return () => { mounted = false; clearInterval(iv); if (abortRef.current) abortRef.current.abort(); };
  }, []);

  const isConfigured = pages[1]?.configured !== false;
  const backendMessage = pages[1]?.message;
  const hasMore = pages[currentPage]?.has_more || false;

  const analysisByHeadline = useMemo(() => {
    const map = new Map();
    const analyses = analysesData?.analyses || [];
    analyses.forEach((a) => {
      if (a.headline) map.set(a.headline.trim().toLowerCase(), a);
    });
    return map;
  }, [analysesData]);

  const liveItems = useMemo(() => {
    // Merge & dedupe across all loaded pages
    const seen = new Set();
    const out = [];
    Object.keys(pages).map(Number).sort((a, b) => a - b).forEach((p) => {
      (pages[p]?.news || []).forEach((n, i) => {
        const key = n.id || `${p}-${i}`;
        if (seen.has(key)) return;
        seen.add(key);
        out.push(mapNewsToEvent(n, out.length, analysisByHeadline));
      });
    });
    return out;
  }, [pages, analysisByHeadline]);

  const filtered = useMemo(() => {
    return liveItems.filter(
      (e) =>
        (cat === "ALL" || e.asset === cat) &&
        (severity === "ALL" || e.severity === severity) &&
        (q === "" || (e.title || "").toLowerCase().includes(q.toLowerCase()))
    );
  }, [cat, severity, q, liveItems]);

  const hasActiveFilter = cat !== "ALL" || severity !== "ALL" || q !== "";
  const emptyMessage = hasActiveFilter
    ? "No events match the current filters."
    : (backendMessage || EMPTY_RECENT_MSG);

  return (
    <div className="space-y-6">
      <SeoHead path="/financial-journal" />
      {!isConfigured && <UnconfiguredBanner />}

      {error && (
        <Card className="flex items-start gap-3 border-fij-red/30 bg-fij-red/5" data-testid="events-error">
          <AlertCircle size={16} className="text-fij-red shrink-0 mt-0.5" />
          <div className="font-mono text-xs text-fij-red">{error}</div>
        </Card>
      )}

      <FilterBar q={q} setQ={setQ} cat={cat} setCat={setCat} severity={severity} setSeverity={setSeverity} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="events-grid">
        {loading && filtered.length === 0 ? (
          Array.from({ length: 6 }).map((_, i) => (
            <Card key={`skel-${i}`} data-testid={`event-skeleton-${i}`} className="animate-pulse space-y-3">
              <div className="h-4 w-16 bg-fij-border/40 rounded-sm" />
              <div className="h-4 w-full bg-fij-border/40 rounded-sm" />
              <div className="h-3 w-5/6 bg-fij-border/40 rounded-sm" />
              <div className="h-3 w-4/6 bg-fij-border/40 rounded-sm" />
              <div className="h-3 w-2/3 bg-fij-border/40 rounded-sm" />
            </Card>
          ))
        ) : (
          filtered.map((e) => (
            <EventCard key={e.id} event={e} />
          ))
        )}
        {!loading && filtered.length === 0 && <EmptyState message={emptyMessage} />}
      </div>

      {hasMore && !loading && (
        <div className="flex justify-center pt-2">
          <button
            data-testid="events-load-more"
            onClick={() => fetchPage(currentPage + 1)}
            disabled={loadingMore}
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wide text-fij-green border border-fij-green/40 hover:bg-fij-green/10 disabled:opacity-50 px-4 py-2 rounded-sm transition-colors"
          >
            {loadingMore ? <Loader2 size={14} className="animate-spin" /> : null}
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="text-center font-mono text-[10px] text-fij-textMuted">
          Showing {filtered.length} of {pages[1]?.total_found || filtered.length} events (≤48h)
        </div>
      )}
    </div>
  );
}
